import { useState, useEffect } from 'react';

const CHANNEL_ID = 'UCuWnBqx7nfF8fdBisvg1ksg';
const UPLOADS_PLAYLIST_ID = 'UUuWnBqx7nfF8fdBisvg1ksg';
const CACHE_KEY = 'latestYouTubeVideo';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

const FALLBACK_VIDEO = {
    videoId: '8tsHYjlQ8xY',
    title: '',
    thumbnail: null,
};

function getCached() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const { data, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp < CACHE_TTL) return data;
    } catch {
        // ignore
    }
    return null;
}

function setCache(data) {
    try {
        localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ data, timestamp: Date.now() })
        );
    } catch {
        // ignore
    }
}

export default function useLatestVideo() {
    const [video, setVideo] = useState(() => getCached() || FALLBACK_VIDEO);
    const [loading, setLoading] = useState(!getCached());

    useEffect(() => {
        const cached = getCached();
        if (cached) {
            setVideo(cached);
            setLoading(false);
            return;
        }

        const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
        if (!apiKey) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        async function fetchLatest() {
            try {
                const url =
                    `https://www.googleapis.com/youtube/v3/playlistItems` +
                    `?part=snippet` +
                    `&playlistId=${UPLOADS_PLAYLIST_ID}` +
                    `&maxResults=1` +
                    `&key=${apiKey}`;

                const res = await fetch(url);
                if (!res.ok) throw new Error(res.statusText);

                const json = await res.json();
                const item = json.items?.[0];
                if (!item) throw new Error('No items');

                const snippet = item.snippet;
                const data = {
                    videoId: snippet.resourceId.videoId,
                    title: snippet.title,
                    thumbnail:
                        snippet.thumbnails?.maxres?.url ||
                        snippet.thumbnails?.high?.url ||
                        snippet.thumbnails?.medium?.url ||
                        null,
                };

                if (!cancelled) {
                    setVideo(data);
                    setCache(data);
                }
            } catch {
                // keep fallback
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchLatest();
        return () => { cancelled = true; };
    }, []);

    return {
        videoId: video.videoId,
        title: video.title,
        thumbnail: video.thumbnail,
        videoUrl: `https://www.youtube.com/watch?v=${video.videoId}`,
        channelUrl: `https://www.youtube.com/channel/${CHANNEL_ID}/videos`,
        loading,
    };
}
